import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Typography,
  Badge,
  Box,
  CircularProgress,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";

const TableAgents = ({ data, search, loading, tableHeight = 520 }) => {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!Array.isArray(data)) {
      setRows([]);
      return;
    }

    if (!search?.trim()) {
      setRows(data);
      return;
    }

    const escaped = search.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(escaped, "i");

    setRows(
      data.filter(
        (agent) =>
          regex.test(agent.machine_name ?? "") ||
          regex.test(agent.ip ?? "") ||
          regex.test(agent.mac ?? "")
      )
    );
  }, [search, data]);

  const emptyLabel = useMemo(() => {
    if (loading) return "Loading...";
    return "No data";
  }, [loading]);

  return (
    <TableContainer
      sx={{
        height: tableHeight,
        minHeight: 0,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <Table stickyHeader sx={tableStyle}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: "40%" }}>Machine</TableCell>
            <TableCell sx={{ width: "30%" }}>IP</TableCell>
            <TableCell sx={{ width: "30%" }}>MAC</TableCell>
          </TableRow>
        </TableHead>

        <TableBody key={search}>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3}>
                <Box
                  sx={{
                    height: 220,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                    opacity: 0.85,
                  }}
                >
                  {loading && <CircularProgress size={18} />}
                  <Typography textAlign="center">{emptyLabel}</Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((agent, idx) => (
              <TableRow key={agent.machine_id ?? agent.mac ?? agent.ip ?? idx}>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Badge
                      invisible={!agent.haveAnomaly}
                      variant="dot"
                      color="error"
                      overlap="circular"
                      sx={{
                        "& .MuiBadge-badge": {
                          transform: "translate(2px, -2px)",
                          boxShadow: "0 0 0 2px rgba(0,0,0,0.12)",
                        },
                      }}
                    />
                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                      {agent.machine_name ?? "-"}
                    </Typography>
                  </Box>
                </TableCell>

                <TableCell>
                  <Typography sx={{ fontSize: 14, opacity: 0.9 }}>
                    {agent.ip ?? "-"}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography sx={{ fontSize: 14, opacity: 0.9 }}>
                    {agent.mac ?? "-"}
                  </Typography>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TableAgents;

const tableStyle = {
  ".MuiTableCell-root": {
    fontSize: 14,
    borderBottom: "1px solid",
    borderColor: "divider",
    py: 1.25,
  },

  ".MuiTableCell-head": {
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontSize: 12,
    backgroundColor: "background.paper",
    borderBottom: "1px solid",
    borderColor: "divider",
    position: "sticky",
    top: 0,
    zIndex: 2,
  },

  ".MuiTableCell-body": {
    fontWeight: 400,
  },

  ".MuiTableRow-root": {
    transition: "background-color .15s ease",
    "&:nth-of-type(even) .MuiTableCell-body": {
      backgroundImage:
        "linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0))",
    },
    "&:hover .MuiTableCell-body": {
      backgroundColor: "action.hover",
    },
  },
};